import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  initGA,
  identifyUser,
  clearIdentity,
  trackPageView,
  trackPageEngagement,
} from "utils/analytics";

// Mounted once near the app root (inside both BrowserRouter and the redux
// Provider) to drive GA4 pageviews, time-on-page, and user identification
// for every route change across the SPA.
const AnalyticsTracker = () => {
  const location = useLocation();
  const profile = useSelector((state) => state.auth.profile);
  const previousPath = useRef(null);
  const pageEnteredAt = useRef(null);

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    if (profile?.id) {
      identifyUser(profile);
    } else {
      clearIdentity();
    }
  }, [profile]);

  useEffect(() => {
    const path = location.pathname + location.search;

    // Guards against React.StrictMode's dev-only double-invoke of effects,
    // and against re-renders that don't actually change the URL — without
    // this, the very first pageview (and its engagement timer) fires twice.
    if (path === previousPath.current) return;

    if (previousPath.current) {
      trackPageEngagement(previousPath.current, performance.now() - pageEnteredAt.current);
    }

    trackPageView(path, document.title);

    previousPath.current = path;
    pageEnteredAt.current = performance.now();
  }, [location]);

  useEffect(() => {
    const sendFinalEngagement = () => {
      if (previousPath.current) {
        trackPageEngagement(previousPath.current, performance.now() - pageEnteredAt.current);
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") sendFinalEngagement();
    };

    window.addEventListener("beforeunload", sendFinalEngagement);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("beforeunload", sendFinalEngagement);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  return null;
};

export default AnalyticsTracker;
